/**
 * conversation-bulk.tsx — Chọn nhiều hội thoại và thao tác hàng loạt.
 *
 * Vào chế độ chọn: RÊ CHUỘT tới dòng là ô tích hiện ra ở đầu dòng (đè lên
 * avatar, kiểu Gmail) — bấm ô đó là chọn. Trên điện thoại không có rê chuột
 * nên vẫn giữ cách NHẤN GIỮ. Cũng vào được từ mục "Chọn nhiều" trong menu ⋯.
 * Khi đang chọn: ô tích hiện ở mọi dòng và bấm dòng là tích/bỏ tích.
 *
 * Thao tác = đúng bộ trong menu ⋯ của từng dòng, áp cho nhiều dòng; backend
 * (POST /conversations/bulk) kiểm lại phạm vi dữ liệu và quyền cho từng cái.
 */
import { useRef, useCallback } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { X, Tag, MailOpen, CheckCheck, Check, Users, Star, Trash2, Loader2, Minus, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/misc'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { apiError } from '@/lib/api-client'
import { useBulkConversations, type BulkConvInput, type ConvTab } from '@/hooks/use-conversations'

// ── Nhấn giữ ───────────────────────────────────────────────────────────────

const HOLD_MS = 500
/** Ngón tay/chuột trượt quá bấy nhiêu px là đang cuộn, không phải giữ. */
const MOVE_TOLERANCE = 8

/**
 * Nhận diện NHẤN GIỮ — CHỈ cho cảm ứng/bút.
 *
 * Chuột đã có cách nhanh hơn (rê tới là hiện ô tích), mà bắt cả chuột thì giữ
 * nút trái vài giây lúc đang đọc cũng nhảy vào chế độ chọn — bất ngờ, khó hiểu.
 * Trả về các handler gắn vào phần tử và `wasLongPress()` — gọi trong onClick để
 * bỏ qua cú click tự phát sinh ngay sau khi thả tay (nếu không, vừa giữ để vào
 * chế độ chọn xong đã lập tức bỏ tích/mở hội thoại).
 */
export function useLongPress(onLongPress: () => void) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const start = useRef<{ x: number; y: number } | null>(null)
  const fired = useRef(false)

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    start.current = null
  }, [])

  const handlers = {
    onPointerDown: (e: React.PointerEvent) => {
      if (e.button !== 0) return
      if (e.pointerType === 'mouse') return // chuột: dùng ô tích hiện khi rê tới
      fired.current = false
      start.current = { x: e.clientX, y: e.clientY }
      timer.current = setTimeout(() => {
        fired.current = true
        timer.current = null
        onLongPress()
      }, HOLD_MS)
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (!start.current) return
      if (Math.abs(e.clientX - start.current.x) > MOVE_TOLERANCE || Math.abs(e.clientY - start.current.y) > MOVE_TOLERANCE) cancel()
    },
    onPointerUp: cancel,
    onPointerLeave: cancel,
    onPointerCancel: cancel,
    // Giữ trên điện thoại mặc định bật menu chuột phải/chọn chữ của trình duyệt.
    onContextMenu: (e: React.MouseEvent) => { if (fired.current) e.preventDefault() },
  }

  const wasLongPress = () => {
    const f = fired.current
    fired.current = false
    return f
  }

  return { handlers, wasLongPress }
}

/**
 * Ô tích của một dòng — nút RIÊNG, đặt đè lên avatar.
 *
 * Phải là phần tử anh em với nút của dòng chứ không nằm trong (lồng nút trong
 * nút là HTML sai, và bấm ô tích sẽ mở luôn hội thoại). Ẩn khi không rê tới,
 * hiện khi rê chuột tới dòng hoặc khi đang ở chế độ chọn.
 */
export function RowCheckbox({
  checked,
  alwaysVisible,
  onToggle,
}: {
  checked: boolean
  /** Đang ở chế độ chọn → luôn hiện, không chờ rê chuột. */
  alwaysVisible: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation('convList')
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={checked ? t('bulk.uncheck') : t('bulk.check')}
      onClick={(e) => { e.stopPropagation(); onToggle() }}
      className={cn(
        // Toạ độ = đúng tâm avatar của dòng (px-3 + avatar h-10, py-2.5).
        'absolute left-[32px] top-[30px] z-10 flex h-5 w-5 -translate-x-1/2 -translate-y-1/2',
        'items-center justify-center rounded border shadow-sm transition-opacity',
        checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-card',
        // Khi chưa hiện thì cũng không ăn click: trên điện thoại (không có rê
        // chuột) chạm vào avatar phải mở hội thoại như thường, không phải chọn.
        alwaysVisible || checked
          ? 'opacity-100'
          : 'pointer-events-none opacity-0 focus-visible:pointer-events-auto focus-visible:opacity-100 ' +
            '[@media(hover:hover)]:group-hover:pointer-events-auto [@media(hover:hover)]:group-hover:opacity-100',
      )}
    >
      {checked && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
    </button>
  )
}

// ── Thanh thao tác ──────────────────────────────────────────────────────────

export function BulkActionBar({
  selectedIds,
  visibleIds,
  tab,
  tags,
  canDelete,
  onSelectAll,
  onExit,
}: {
  selectedIds: string[]
  visibleIds: string[]
  tab: ConvTab
  tags: Array<{ id: string; name: string; color: string }>
  canDelete: boolean
  onSelectAll: (all: boolean) => void
  onExit: () => void
}) {
  const { t } = useTranslation('convList')
  const bulk = useBulkConversations()
  const n = selectedIds.length
  const allChecked = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id))
  const someChecked = n > 0 && !allChecked

  const run = (input: Omit<BulkConvInput, 'ids'>, doneMsg: (affected: number) => string, exitAfter = true) => {
    if (n === 0) return
    bulk.mutate(
      { ...input, ids: selectedIds },
      {
        onSuccess: (r) => {
          toast.success(doneMsg(r.affected) + (r.skipped ? t('bulk.skipped', { count: r.skipped }) : ''))
          if (exitAfter) onExit()
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )
  }

  const moveTo: ConvTab = tab === 'internal' ? 'main' : 'internal'

  return (
    <div className="space-y-1.5 border-b bg-primary/5 px-3 py-2">
      <div className="flex items-center gap-2">
        <Checkbox
          checked={allChecked ? true : someChecked ? 'indeterminate' : false}
          onCheckedChange={() => onSelectAll(!allChecked)}
          aria-label={t('bulk.selectAllAria')}
        />
        <span className="flex-1 text-sm font-medium">
          {n > 0 ? t('common:misc.selected', { count: n }) : t('bulk.check')}
          {bulk.isPending && <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </span>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onExit} title={t('bulk.exitTitle')}>
          <X className="h-3.5 w-3.5" /> {t('common:actions.cancel')}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {/* Nhãn: gắn / gỡ cho mọi liên hệ của các hội thoại đã chọn */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" disabled={n === 0 || bulk.isPending}>
              <Tag className="h-3.5 w-3.5" /> {t('common:fields.tags')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-52">
            {tags.length === 0 ? (
              <div className="px-2 py-3 text-xs text-muted-foreground">{t('bulk.noTags')}</div>
            ) : (
              <>
                <DropdownMenuLabel className="text-xs">{t('bulk.applyTo', { count: n })}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(['add_tag', 'remove_tag'] as const).map((act) => (
                  <DropdownMenuSub key={act}>
                    <DropdownMenuSubTrigger className="gap-2">
                      {act === 'add_tag' ? <Plus className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                      {act === 'add_tag' ? t('bulk.addTag') : t('bulk.removeTag')}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="max-h-80 w-56 overflow-y-auto">
                      {tags.map((tg) => (
                        <DropdownMenuItem
                          key={tg.id}
                          className="gap-2"
                          onSelect={() => run(
                            { action: act, tag: tg.name },
                            (a) => t(act === 'add_tag' ? 'bulk.tagAdded' : 'bulk.tagRemoved', { tag: tg.name, count: a }),
                            false, // gắn nhiều nhãn liên tiếp mà không phải chọn lại
                          )}
                        >
                          <Tag className="h-4 w-4 shrink-0" style={{ color: tg.color, fill: tg.color }} />
                          <span className="truncate">{tg.name}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ))}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" disabled={n === 0 || bulk.isPending}
          onClick={() => run({ action: 'mark_read' }, (a) => t('bulk.markedRead', { count: a }))}
        >
          <CheckCheck className="h-3.5 w-3.5" /> {t('bulk.read')}
        </Button>
        <Button
          variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" disabled={n === 0 || bulk.isPending}
          onClick={() => run({ action: 'mark_unread' }, (a) => t('bulk.markedUnread', { count: a }))}
        >
          <MailOpen className="h-3.5 w-3.5" /> {t('shared.unread')}
        </Button>
        <Button
          variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" disabled={n === 0 || bulk.isPending}
          onClick={() => run(
            { action: 'set_tab', tab: moveTo },
            (a) => t(moveTo === 'internal' ? 'bulk.movedInternal' : 'bulk.movedMain', { count: a }),
          )}
        >
          {moveTo === 'internal' ? <Users className="h-3.5 w-3.5" /> : <Star className="h-3.5 w-3.5" />}
          {moveTo === 'internal' ? t('bulk.toInternal') : t('bulk.toMain')}
        </Button>
        {canDelete && (
          <Button
            variant="outline" size="sm"
            className="h-7 gap-1 px-2 text-xs text-destructive hover:text-destructive"
            disabled={n === 0 || bulk.isPending}
            onClick={() => {
              if (!window.confirm(t('bulk.confirmDelete', { count: n }))) return
              run({ action: 'delete' }, (a) => t('bulk.deleted', { count: a }))
            }}
          >
            <Trash2 className="h-3.5 w-3.5" /> {t('common:actions.delete')}
          </Button>
        )}
      </div>
    </div>
  )
}
